import { Component, type ErrorInfo, type ReactNode } from 'react';
import { clearAutosave } from '../autosave';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Top-level error boundary so a crash in any component doesn't leave the
 * user staring at a blank canvas. Catches the throw, prints the stack, and
 * offers a few recovery actions:
 *   • Reload the window (clean slate, autosave intact)
 *   • Discard the autosaved draft and reload (use this when the autosaved
 *     project itself is what's crashing)
 *
 * Without this, a render-time error in any descendant React component
 * silently unmounts the whole tree — which looks identical to a hung app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to devtools so the stack is preserved for inspection.
    // eslint-disable-next-line no-console
    console.error('Skematiq crash:', error, info);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-canvas)',
          color: 'var(--text)',
          padding: '40px 60px',
          overflowY: 'auto',
          fontFamily: 'ui-monospace, Consolas, monospace',
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        <div style={{ maxWidth: 900 }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: '#dc2626',
              marginBottom: 12,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Skematiq crashed
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-muted)',
              marginBottom: 20,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Something in the project file caused a render error. Pick a recovery
            option below — your library and any saved <code>.skema.json</code>
            file on disk are untouched.
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={btnPrimary}
            >
              Reload window
            </button>
            <button
              onClick={() => {
                clearAutosave();
                window.location.reload();
              }}
              style={btnDanger}
              title="Use this if the crash happens every time you launch — it clears the autosaved draft and starts fresh."
            >
              Discard autosave & reload
            </button>
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'var(--text-muted)',
              marginBottom: 6,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Error
          </div>
          <pre
            style={{
              background: 'var(--panel)',
              border: '1px solid var(--panel-border)',
              borderRadius: 'var(--radius-sm)',
              padding: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              marginBottom: 16,
            }}
          >
            {error.name}: {error.message}
          </pre>

          {error.stack && (
            <details>
              <summary
                style={{
                  cursor: 'pointer',
                  marginBottom: 6,
                  color: 'var(--text-muted)',
                  fontFamily: 'system-ui, sans-serif',
                }}
              >
                Stack trace
              </summary>
              <pre
                style={{
                  background: 'var(--panel)',
                  border: '1px solid var(--panel-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                  fontSize: 11,
                  color: 'var(--text-muted)',
                }}
              >
                {error.stack}
              </pre>
            </details>
          )}

          {info?.componentStack && (
            <details style={{ marginTop: 12 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  marginBottom: 6,
                  color: 'var(--text-muted)',
                  fontFamily: 'system-ui, sans-serif',
                }}
              >
                Component tree
              </summary>
              <pre
                style={{
                  background: 'var(--panel)',
                  border: '1px solid var(--panel-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                  fontSize: 11,
                  color: 'var(--text-muted)',
                }}
              >
                {info.componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'system-ui, sans-serif',
};

const btnDanger: React.CSSProperties = {
  background: '#dc2626',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'system-ui, sans-serif',
};
